import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useNetwork } from '../../WalletProvider';
import logger from '../../utils/logger';

// Only use /app prefix on yobitch.fun production domain
const isProductionDomain =
  typeof window !== 'undefined' && window.location.hostname === 'yobitch.fun';
const pathPrefix = isProductionDomain ? '/app' : '';

// Image-based icons with error handling
const IconImage = ({ src, alt, size = 32, style }) => {
  const [imgError, setImgError] = useState(false);
  const [imgLoading, setImgLoading] = useState(true);

  const handleError = useCallback(() => {
    setImgError(true);
    setImgLoading(false);
  }, []);

  const handleLoad = useCallback(() => {
    setImgLoading(false);
  }, []);

  if (imgError) {
    return (
      <div
        style={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '4px',
          fontSize: size * 0.4,
          color: 'rgba(255, 255, 255, 0.5)',
          ...style,
        }}
      >
        ?
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      onError={handleError}
      onLoad={handleLoad}
      style={{
        width: size,
        height: size,
        objectFit: 'contain',
        opacity: imgLoading ? 0.5 : 1,
        transition: 'opacity 0.2s ease',
        ...style,
      }}
    />
  );
};

const BottomNav = () => {
  // ALL HOOKS MUST BE CALLED FIRST - in the same order every render
  const navigate = useNavigate();
  const location = useLocation();
  const [launchMode] = useLocalStorage('pf-launch-mode', 'basic');
  const [navigating, setNavigating] = useState(false);
  const { network } = useNetwork();
  const isDevnet = network === 'devnet';

  // Memoize tabs to prevent recreation on every render
  // When devnet is active, Cook tab goes to Lab instead
  const tabs = useMemo(
    () => [
      {
        id: 'launch',
        path: isDevnet ? `${pathPrefix}/lab` : `${pathPrefix}/launch`,
        icon: '/images/icon_cook.png',
        label: isDevnet ? 'Lab' : 'Cook',
      },
      {
        id: 'trends',
        path: `${pathPrefix}/trends`,
        icon: '/images/icon_product.png',
        label: 'Alpha',
      },
      {
        id: 'portfolio',
        path: `${pathPrefix}/portfolio`,
        icon: '/images/icon_empire.png',
        label: 'Empire',
      },
      { id: 'tools', path: `${pathPrefix}/tools`, icon: '/images/icon_lab.png', label: 'Tools' },
      {
        id: 'settings',
        path: `${pathPrefix}/settings`,
        icon: '/images/icon_heisenberg.png',
        label: 'Config',
      },
    ],
    [isDevnet]
  );

  // Preserve demo mode query param when navigating
  const getPathWithParams = useCallback(
    path => {
      try {
        if (!path) return '/';
        const params = new URLSearchParams(location.search);
        const demoMode = params.get('demo');
        if (demoMode) {
          return `${path}?demo=${demoMode}`;
        }
        return path;
      } catch (e) {
        return path || '/';
      }
    },
    [location.search]
  );

  const isActive = useCallback(
    path => {
      try {
        if (!path || !location.pathname) return false;
        const currentPath = location.pathname;
        if (currentPath === path) return true;
        if (currentPath.startsWith(path + '/')) return true;
        return false;
      } catch (e) {
        return false;
      }
    },
    [location.pathname]
  );

  // Get mode color with fallback
  const getModeColor = useCallback(() => {
    try {
      switch (launchMode) {
        case 'ultra':
          return '#00D9FF';
        case 'max':
          return '#FFD700';
        case 'pro':
          return '#9945FF';
        default:
          return 'var(--accent, #1E7B34)';
      }
    } catch (e) {
      return '#1E7B34';
    }
  }, [launchMode]);

  const handleNavigation = useCallback(
    path => {
      if (navigating) return;

      try {
        setNavigating(true);
        const targetPath = getPathWithParams(path);
        navigate(targetPath);

        // Reset navigating state after a short delay
        setTimeout(() => setNavigating(false), 300);
      } catch (error) {
        logger.error('Navigation error', { error: error.message });
        setNavigating(false);
      }
    },
    [navigate, getPathWithParams, navigating]
  );

  // Safety check for tabs array - but hooks already called above
  if (!tabs || !Array.isArray(tabs) || tabs.length === 0) {
    return (
      <nav
        className="bottom-navigation"
        role="navigation"
        aria-label="Bottom navigation"
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '80px',
          minHeight: '80px',
          maxHeight: '80px',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          background: 'rgba(0, 0, 0, 0.98)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          zIndex: 1000,
        }}
      />
    );
  }

  return (
    <nav
      className="bottom-navigation"
      role="navigation"
      aria-label="Bottom navigation"
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '80px',
        minHeight: '80px',
        maxHeight: '80px',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        background: 'rgba(0, 0, 0, 0.98)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderTop: 'none',
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        paddingTop: '8px',
        zIndex: 1000,
        flexShrink: 0,
        boxSizing: 'border-box',
      }}
    >
      {tabs.map((tab, idx) => {
        try {
          if (!tab || typeof tab !== 'object') return null;

          const { id, path, icon, label } = tab;
          if (!id && !path) return null;

          const active = isActive(path);
          const isLaunch = id === 'launch';
          const modeColor = getModeColor();

          return (
            <button
              key={id || `tab-${idx}`}
              onClick={() => {
                if (path && !navigating) {
                  handleNavigation(path);
                }
              }}
              disabled={navigating || !path}
              aria-label={`Navigate to ${label || id || 'page'}`}
              aria-current={active ? 'page' : undefined}
              type="button"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                padding: '6px 8px',
                background: 'transparent',
                border: 'none',
                borderLeft: idx === 0 ? 'none' : '1px solid rgba(255, 255, 255, 0.06)',
                cursor: navigating || !path ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                minWidth: '60px',
                minHeight: '56px',
                flex: 1,
                maxWidth: '80px',
                opacity: navigating ? 0.7 : 1,
              }}
              onMouseEnter={e => {
                if (!navigating && path) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: active
                    ? isLaunch
                      ? `${modeColor}20`
                      : 'rgba(200, 180, 50, 0.2)'
                    : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                }}
              >
                <IconImage
                  src={icon || '/images/icon_cook.png'}
                  alt={label || id || 'Navigation icon'}
                  size={32}
                  style={{
                    opacity: active ? 1 : 0.6,
                    transition: 'opacity 0.2s ease',
                    filter: active ? 'none' : 'grayscale(30%)',
                  }}
                />
                {/* Mode indicator for launch tab */}
                {isLaunch && launchMode && launchMode !== 'basic' && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '-2px',
                      right: '0px',
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: modeColor,
                      border: '1px solid rgba(10, 10, 15, 0.98)',
                      zIndex: 1,
                    }}
                  />
                )}
              </div>
              {/* Label text */}
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: active ? '600' : '500',
                  color: active
                    ? isLaunch
                      ? modeColor
                      : 'rgba(200, 180, 50, 0.9)'
                    : 'rgba(255, 255, 255, 0.5)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  transition: 'all 0.2s ease',
                  lineHeight: 1,
                  marginTop: '2px',
                }}
              >
                {label || id}
              </span>
            </button>
          );
        } catch (error) {
          logger.error('Error rendering tab', { tabId: tab?.id, error: error.message });
          return null;
        }
      })}
    </nav>
  );
};

export default BottomNav;
